import type { PropsWithChildren } from "react";
import { useState } from "react";
import { Link, useLocalSearchParams } from "expo-router";
import { FormTitle } from "@ptsd/uikit";
import { useTranslation } from "react-i18next";
import {
  Anchor,
  ScrollView,
  Spinner,
  Text,
  useMedia,
  View,
  XStack,
  YStack,
} from "tamagui";
import { LinearGradient } from "tamagui/linear-gradient";

import { api } from "~/utils/api";
import img from "~/assets/signup/signup-1.png";
import {
  AuthBaseForm,
  AuthContainer,
  AuthContent,
  AuthImage,
} from "~/component/feature/auth/wrappers";
import { GuestErrorScreen } from "~/component/feature/guest/guest-error-screen";

const OrganizationPageLayout = ({ children }: PropsWithChildren) => (
  <AuthContainer>
    <AuthContent>{children}</AuthContent>
    <AuthImage src={img} />
  </AuthContainer>
);

const OrganizationDetails = ({ name }: { name: string }) => {
  const { t } = useTranslation("organizationSignup");
  const { sm } = useMedia();

  return (
    <YStack gap="$2" width="100%">
      <Text
        color="$gray-primary-900"
        fontFamily="$body"
        fontSize={sm ? "$3" : "$4"}
        fontWeight="$5"
        lineHeight={sm ? "$3" : "$4"}
      >
        {name}
      </Text>
      <FormTitle color="$gray-primary-900">{t("selectGroup")}</FormTitle>
    </YStack>
  );
};

const GroupSignupLink = ({
  name,
  signupSlug,
}: {
  name: string;
  signupSlug: string;
}) => {
  const { sm } = useMedia();

  return (
    <Link href={`/s/${signupSlug}`} asChild>
      <Anchor
        data-id="organization-group-link"
        unstyled
        alignItems="center"
        backgroundColor="white"
        borderColor="$gray-800"
        borderRadius="$4.5"
        borderWidth="$0.2"
        cursor="pointer"
        display="flex"
        flexDirection="row"
        minHeight={sm ? "$7.4" : "$8.5"}
        paddingHorizontal="$3"
        textDecorationLine="none"
        focusStyle={{
          backgroundColor: "$gray-50",
          borderColor: "$gray-primary-900",
        }}
        hoverStyle={{
          backgroundColor: "$gray-50",
          borderColor: "$gray-secondary-400",
        }}
        pressStyle={{ backgroundColor: "$gray-200" }}
      >
        <XStack alignItems="center" gap={sm ? "$2" : "$1.5"} flex={1}>
          <Text
            color="$gray-primary-900"
            flex={1}
            fontFamily={sm ? "$body" : "$subtitle"}
            fontSize={sm ? "$3" : "$5"}
            fontWeight="$6"
            lineHeight={sm ? "$1" : "$4"}
          >
            {name}
          </Text>
        </XStack>
      </Anchor>
    </Link>
  );
};

const OrganizationGroups = ({
  groups,
}: {
  groups: { name: string; signupSlug: string }[];
}) => {
  const { t } = useTranslation("organizationSignup");
  const { sm } = useMedia();
  const [contentHeight, setContentHeight] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(0);
  const [scrollOffset, setScrollOffset] = useState(0);

  const isScrollable = contentHeight > viewportHeight + 1;
  const showTopShadow = isScrollable && scrollOffset >= 10;
  const showBottomShadow =
    isScrollable && scrollOffset < contentHeight - viewportHeight - 10;

  return (
    <View gap="$3" width="100%">
      {groups.length === 0 ? (
        <Text
          color="$gray-primary-900"
          fontFamily="$body"
          fontSize={sm ? "$3" : "$4"}
          lineHeight={sm ? "$3" : "$4"}
        >
          {t("empty")}
        </Text>
      ) : (
        <YStack maxHeight={440} position="relative">
          <ScrollView
            data-id="organization-groups-scroll"
            maxHeight={440}
            onContentSizeChange={(_, height) => setContentHeight(height)}
            onLayout={(event) =>
              setViewportHeight(event.nativeEvent.layout.height)
            }
            onScroll={(event) =>
              setScrollOffset(event.nativeEvent.contentOffset.y)
            }
            scrollEventThrottle={16}
            showsVerticalScrollIndicator
          >
            <YStack gap={sm ? "$1.5" : "$2"}>
              {groups.map((group) => (
                <GroupSignupLink key={group.signupSlug} {...group} />
              ))}
            </YStack>
          </ScrollView>
          {showTopShadow && (
            <LinearGradient
              data-id="organization-groups-top-shadow"
              colors={["white", "transparent"]}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: 80,
                pointerEvents: "none",
                zIndex: 50,
              }}
            />
          )}
          {showBottomShadow && (
            <LinearGradient
              data-id="organization-groups-bottom-shadow"
              colors={["transparent", "white"]}
              style={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                height: 80,
                pointerEvents: "none",
                zIndex: 50,
              }}
            />
          )}
        </YStack>
      )}
    </View>
  );
};

export default function OrganizationSignupPage() {
  const { t } = useTranslation("organizationSignup");
  const params = useLocalSearchParams<{ slug?: string | string[] }>();
  const slug = Array.isArray(params.slug) ? params.slug[0] : params.slug;
  const { data, isError, isLoading } = api.organization.bySignupSlug.useQuery(
    { slug: slug ?? "" },
    {
      enabled: Boolean(slug),
      refetchInterval: false,
      refetchIntervalInBackground: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    },
  );

  if (!slug) {
    return (
      <OrganizationPageLayout>
        <View maxWidth={413} width="100%">
          <GuestErrorScreen text={t("unavailable")} withBackBtn />
        </View>
      </OrganizationPageLayout>
    );
  }

  if (isLoading) {
    return (
      <OrganizationPageLayout>
        <View
          alignItems="center"
          justifyContent="center"
          maxWidth={413}
          minHeight={300}
          width="100%"
        >
          <Spinner color="$green-500" size="large" />
        </View>
      </OrganizationPageLayout>
    );
  }

  if (isError || !data) {
    return (
      <OrganizationPageLayout>
        <View maxWidth={413} width="100%">
          <GuestErrorScreen text={t("unavailable")} withBackBtn />
        </View>
      </OrganizationPageLayout>
    );
  }

  return (
    <OrganizationPageLayout>
      <AuthBaseForm>
        <OrganizationDetails name={data.name} />
        <OrganizationGroups groups={data.groups} />
      </AuthBaseForm>
    </OrganizationPageLayout>
  );
}
